import React from "react";
import ReactDOM from "react-dom/client";
import axios from "axios";
import "@/index.css";
import App from "@/App";
import { installAxiosInterceptor, installGlobalHandlers, logError } from "@/lib/errorLogger";
import { installToastLogger } from "@/lib/toastLogger";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { queryClient, queryPersister, PERSIST_BUSTER } from "@/lib/queryClient";

// Global axios interceptor: attach session token to all requests
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem("session_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Global response interceptor: handle auth failures and structured errors
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    const detail = error.response?.data?.detail;
    const code = typeof detail === "object" ? detail?.code : null;
    
    if (error.response?.status === 401 && !error.config?.url?.includes("/auth/")) {
      if (code === "AUTH_EXPIRED" || code === "AUTH_INVALID" || code === "AUTH_REQUIRED") {
        localStorage.removeItem("session_token");
        localStorage.removeItem("user_id");
        localStorage.removeItem("user_name");
        localStorage.removeItem("user_role");
        window.location.reload();
      }
    }
    return Promise.reject(error);
  }
);

// Error logging: auto-log all failed API calls + unhandled JS errors
installAxiosInterceptor(axios);
installGlobalHandlers();
installToastLogger();

// Catch unhandled chunk load errors globally (stale deploy safety net)
// iter 389 — a plain reload isn't enough when the service worker has
// cached the stale HTML / poisoned chunks: nuke SW + caches first so the
// reload actually fetches fresh files.
async function healStaleBundle() {
  try {
    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
    }
    if ('caches' in window) {
      const names = await caches.keys();
      await Promise.all(names.map((n) => caches.delete(n)));
    }
  } catch { /* best effort */ }
  window.location.reload();
}
window.addEventListener('error', (e) => {
  if (e.message && (
    e.message.includes('Loading chunk') ||
    e.message.includes('ChunkLoadError') ||
    e.message.includes("Unexpected token '<'")
  )) {
    const last = sessionStorage.getItem('chunk_reload');
    if (!last || Date.now() - parseInt(last, 10) > 30000) {
      sessionStorage.setItem('chunk_reload', Date.now().toString());
      // iter 429 — the global stale-bundle heal is a "retry page" too; log it before reloading
      logError({ action: "stale_bundle_reload", source: "chunk", error: e.error || { message: e.message }, extra: { where: "healStaleBundle" } });
      healStaleBundle();
    }
  }
});

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{
        persister: queryPersister,
        // Bump PERSIST_BUSTER in lib/queryClient.js to invalidate all
        // cached data after a breaking schema change.
        buster: PERSIST_BUSTER,
        maxAge: 1000 * 60 * 60 * 24, // 24h cache
      }}
    >
      <App />
    </PersistQueryClientProvider>
  </React.StrictMode>,
);
